import { memo, useCallback, useMemo, useRef } from "react";
import { motion, useDragControls, useMotionValue } from "framer-motion";

import { useRect } from "~/hooks/useRect";
import { supportsTouch } from "~/utils/helpers";

import { useSheetModal } from "./SheetModal";

import type { HTMLMotionProps } from "framer-motion";
import type { ReactNode, PointerEvent, CSSProperties } from "react";

export interface SheetModalScrollViewProps {
  children?: ReactNode;
}

// TODO create an imperative handle for scrolling content into view

export const SheetModalScrollView = memo(function SheetModalScrollView({
  children,
}: SheetModalScrollViewProps) {
  const { dragControls, disableGestures } = useSheetModal();

  const internalControls = useDragControls();
  const y = useMotionValue(0);

  const [containerRef, ContainerDOMRect] = useRect<HTMLDivElement>();
  const [contentRef, ContentDOMRect] = useRect<HTMLDivElement>();

  const touched = useRef(false);
  const moving = useRef(false);
  const pageY = useRef(0);

  const style: CSSProperties = useMemo(
    () => ({
      touchAction: !disableGestures ? "none" : undefined,
    }),
    [disableGestures],
  );

  const handlePointerDown = useCallback(
    (e: PointerEvent) => {
      e.preventDefault();
      touched.current = true;
      y.stop();
    },
    [y],
  );

  const handlePointerMove = useCallback(
    (e: PointerEvent) => {
      // Android fires a pointermove event simultaneously
      // with pointerdown so we have to disregard the first
      // fire and only handle the first _real_ move event.
      // https://bugs.chromium.org/p/chromium/issues/detail?id=963024
      if (touched.current && moving.current) return;

      const movementY = pageY.current ? e.pageY - pageY.current : 0;

      pageY.current = e.pageY;

      if (!moving.current && movementY !== 0) {
        const scrollingLocked = movementY > 0 && y.get() >= 0;

        if (scrollingLocked) {
          dragControls?.start(e);
        } else {
          internalControls.start(e);
        }

        moving.current = true;
      }
    },
    [dragControls, internalControls, y],
  );

  const handlePointerUp = useCallback((e: PointerEvent) => {
    touched.current = false;
    moving.current = false;
    pageY.current = 0;
  }, []);

  const motionProps = useMemo<Partial<HTMLMotionProps<"div">>>(() => {
    return {
      drag: "y",
      dragListener: false,
      dragControls: internalControls,
      dragConstraints: {
        top:
          -1 * (containerRef.current?.scrollHeight ?? 0) +
          ContainerDOMRect.height,
        bottom: 0,
      },
      style: {
        y,
        ...style,
      },
      transition: {
        type: "spring",
      },
    };
  }, [ContainerDOMRect.height, containerRef, internalControls, style, y]);

  const renderedContent = useMemo(() => {
    if (supportsTouch()) {
      return (
        <>
          <div style={{ height: ContentDOMRect.height }} />

          <motion.div
            {...motionProps}
            ref={contentRef}
            className="SheetModalScrollView__content"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
          >
            {children}
          </motion.div>
        </>
      );
    }

    return children;
  }, [
    ContentDOMRect.height,
    children,
    contentRef,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    motionProps,
  ]);

  return (
    <div
      ref={containerRef}
      className="SheetModalScrollView"
      data-touch={supportsTouch()}
      style={style}
    >
      {renderedContent}
    </div>
  );
});
